import { useCallback, useEffect, useState } from "react";

const KEY = "svc_estimate";
const EVENT = "hireeva:services-estimate";

export type ServicesEstimate = {
  team: number;
  hours: number;
  jobValue: number;
  low: number;
  high: number;
};

function read(): ServicesEstimate | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (typeof v?.team !== "number" || typeof v?.hours !== "number") return null;
    return v as ServicesEstimate;
  } catch {
    return null;
  }
}

/** Human-readable one-liner for the Close lead note. */
export function estimateNote(e: ServicesEstimate | null): string {
  if (!e) return "";
  const money = (n: number) => "$" + Math.round(n).toLocaleString("en-US");
  return `Payback estimator: ${e.team} people, ${e.hours} hrs/week each lost to admin, ~${money(e.jobValue)} avg job value → estimated ${money(e.low)}–${money(e.high)}/mo upside.`;
}

export function readServicesEstimate() {
  return read();
}

/**
 * Payback-estimator inputs, kept for the session so the call modal and the
 * outline form can carry the visitor's own numbers into the lead.
 */
export function useServicesEstimate() {
  const [estimate, setEstimateState] = useState<ServicesEstimate | null>(() => read());

  useEffect(() => {
    const onSync = () => setEstimateState(read());
    window.addEventListener(EVENT, onSync);
    return () => window.removeEventListener(EVENT, onSync);
  }, []);

  const setEstimate = useCallback((next: ServicesEstimate | null) => {
    try {
      if (next) window.sessionStorage.setItem(KEY, JSON.stringify(next));
      else window.sessionStorage.removeItem(KEY);
    } catch {
      /* private mode — in-memory only */
    }
    setEstimateState(next);
    window.dispatchEvent(new CustomEvent(EVENT));
  }, []);

  return { estimate, setEstimate };
}
